'use client';

import { format } from 'date-fns';
import { CalendarIcon, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DayPicker, useDayPicker, type CalendarMonth } from 'react-day-picker';

import { Button } from '../../primitives/button';
import { Label } from '../../primitives/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../primitives/popover';
import { useLocalization } from '../../theme';
import { cn } from '../../utils/cn';
import { useWheelScrollFix } from '../../utils/use-wheel-scroll-fix';

export interface UzModernDatePickerProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  value?: Date | null;
  defaultValue?: Date | null;
  onValueChange?: (value: Date | null) => void;
  /** Disables specific dates. (date) => boolean. */
  disabled?: boolean | ((date: Date) => boolean);
  /**
   * Earliest selectable date. Days before this are greyed out AND the
   * calendar opens on `minDate`'s month if it's in the future - so a
   * field bounded by e.g. September only doesn't open on today's
   * August with every day disabled.
   */
  minDate?: Date;
  /** Latest selectable date. Days after this are greyed out. */
  maxDate?: Date;
  /** Display format on the trigger. Defaults to AppConfig's dateFormat. */
  displayFormat?: string;
  /** Right-side icon override. */
  icon?: ReactNode;

  // ----- Feature toggles (all default to a modern, polished UX) -----
  /**
   * Show month + year quick-jump dropdowns in the caption row. When `false`,
   * the caption is a static month/year label (operator navigates with the
   * prev/next chevrons only).
   * @default true
   */
  showMonthYearDropdown?: boolean;
  /** Lower year bound for the year dropdown (inclusive). @default 1950 */
  fromYear?: number;
  /** Upper year bound for the year dropdown (inclusive). @default current year + 15 */
  toYear?: number;
  /**
   * Show a footer row with Today + Clear quick-actions. Useful in forms;
   * pass `false` for ultra-compact placements.
   * @default true
   */
  showFooter?: boolean;
  /** Week start. 0 = Sunday, 1 = Monday. @default 0 */
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /**
   * Close the popover on day-select. Set `false` for "stays open while
   * fine-tuning" UX (e.g. a step-flow that needs explicit Apply).
   * @default true
   */
  closeOnSelect?: boolean;

  containerClassName?: string;
  triggerClassName?: string;
}

const startOfDay = (d: Date): Date => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};
const endOfDay = (d: Date): Date => {
  const c = new Date(d);
  c.setHours(23, 59, 59, 999);
  return c;
};

const MONTH_NAMES_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const MONTH_NAMES_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Custom MonthCaption - replaces the default caption row entirely so we get
 * popover-driven month/year dropdowns (instead of v10's native `<select>`s
 * via `captionLayout="dropdown"`). Sits centered between the prev/next nav
 * chevrons, which the parent positions absolutely.
 *
 * Implemented as an inner component so `useDayPicker()` (which exposes
 * `goToMonth`) is in scope - that hook only resolves when called inside the
 * DayPicker subtree.
 */
const ModernMonthCaption = ({
  calendarMonth,
  fromYear,
  toYear,
  showDropdown,
}: {
  calendarMonth: CalendarMonth;
  fromYear: number;
  toYear: number;
  showDropdown: boolean;
}): React.JSX.Element => {
  const { goToMonth } = useDayPicker();
  const current = calendarMonth.date;
  const currentMonth = current.getMonth();
  const currentYear = current.getFullYear();

  if (!showDropdown) {
    return (
      <div className="flex h-9 items-center justify-center">
        <span className="text-sm font-semibold text-zinc-900">
          {MONTH_NAMES_LONG[currentMonth]} {currentYear}
        </span>
      </div>
    );
  }

  const handleMonth = (m: number): void => {
    const next = new Date(currentYear, m, 1);
    goToMonth(next);
  };
  const handleYear = (y: number): void => {
    const next = new Date(y, currentMonth, 1);
    goToMonth(next);
  };

  return (
    <div className="flex h-9 items-center justify-center gap-1.5">
      <MonthChip currentMonth={currentMonth} onPick={handleMonth} />
      <YearChip currentYear={currentYear} onPick={handleYear} fromYear={fromYear} toYear={toYear} />
    </div>
  );
};

interface MonthChipProps {
  currentMonth: number;
  onPick: (month: number) => void;
}

const MonthChip = ({ currentMonth, onPick }: MonthChipProps): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Pick month"
          className="inline-flex h-8 items-center gap-1 rounded-md bg-transparent px-2.5 text-sm font-semibold text-zinc-900 transition-colors hover:bg-zinc-100 focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:outline-none"
        >
          {MONTH_NAMES_LONG[currentMonth]}
          <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        className="w-[200px] rounded-lg border border-zinc-200 p-1.5 shadow-lg"
      >
        <div className="grid grid-cols-3 gap-1">
          {MONTH_NAMES_SHORT.map((name, idx) => {
            const active = idx === currentMonth;
            return (
              <button
                key={name}
                type="button"
                onClick={() => {
                  onPick(idx);
                  setOpen(false);
                }}
                className={cn(
                  'rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                  active
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900',
                )}
              >
                {name}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

interface YearChipProps {
  currentYear: number;
  onPick: (year: number) => void;
  fromYear: number;
  toYear: number;
}

const YearChip = ({ currentYear, onPick, fromYear, toYear }: YearChipProps): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const years = useMemo(() => {
    const list: number[] = [];
    for (let y = toYear; y >= fromYear; y--) list.push(y);
    return list;
  }, [fromYear, toYear]);

  // Nested inside the main date popover (often inside a Drawer). The outer
  // PopoverContent wheel fix does not cover this portaled sibling, so bind
  // the scroll fix directly on the year list while it is open.
  useWheelScrollFix(scrollRef, open);

  // Auto-scroll the currently-selected year into view when the popover opens
  // so the operator doesn't have to hunt through a 75-year list every time.
  useEffect(() => {
    if (!open) return;
    const node = scrollRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    node?.scrollIntoView({ block: 'center' });
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Pick year"
          className="inline-flex h-8 items-center gap-1 rounded-md bg-transparent px-2.5 text-sm font-semibold text-zinc-900 transition-colors hover:bg-zinc-100 focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:outline-none"
        >
          {currentYear}
          <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        className="w-[112px] rounded-lg border border-zinc-200 p-1 shadow-lg"
      >
        <div
          ref={scrollRef}
          className="max-h-[224px] overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]"
        >
          <div className="flex flex-col gap-0.5">
            {years.map((y) => {
              const active = y === currentYear;
              return (
                <button
                  key={y}
                  type="button"
                  data-active={active}
                  onClick={() => {
                    onPick(y);
                    setOpen(false);
                  }}
                  className={cn(
                    'rounded-md px-2 py-1.5 text-center text-xs font-medium transition-colors',
                    active
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900',
                  )}
                >
                  {y}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Single-date picker with a modern visual treatment - popover-driven
 * month / year jump chips (NOT native HTML selects), tight rounded day
 * cells, soft hover, strong blue selected fill, accessible focus rings.
 *
 * Self-contained: does NOT use the shared `Calendar` primitive, so styling
 * tweaks here can never regress UzDatePicker / UzDateRangePicker.
 *
 * Every visual feature is a prop:
 *   - `showMonthYearDropdown` (default true)  - month/year chips
 *   - `showFooter`            (default true)  - Today / Clear footer
 *   - `fromYear` / `toYear`                   - year-dropdown bounds
 *   - `weekStartsOn`          (default 0)     - Sunday vs Monday start
 *   - `closeOnSelect`         (default true)  - auto-close vs stay-open
 */
export const UzModernDatePicker = ({
  label,
  hint,
  error,
  required,
  placeholder = 'Pick a date',
  value,
  defaultValue,
  onValueChange,
  disabled,
  minDate,
  maxDate,
  displayFormat,
  icon,
  showMonthYearDropdown = true,
  fromYear = 1950,
  toYear,
  showFooter = true,
  weekStartsOn = 0,
  closeOnSelect = true,
  containerClassName,
  triggerClassName,
}: UzModernDatePickerProps): React.JSX.Element => {
  const localization = useLocalization();
  const fmt = displayFormat ?? localization.dateFormat;

  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<Date | null>(defaultValue ?? null);
  const isControlled = value !== undefined;
  const selected = isControlled ? value : internal;

  const commit = (next: Date | null): void => {
    if (!isControlled) setInternal(next);
    onValueChange?.(next);
  };

  const handleSelect = (date: Date | undefined): void => {
    commit(date ?? null);
    if (closeOnSelect) setOpen(false);
  };

  // Year-dropdown bounds. Compute per-render so a long-lived popover doesn't
  // drift across a year boundary.
  const nowYear = new Date().getFullYear();
  const resolvedToYear = toYear ?? nowYear + 15;
  const startMonth = useMemo(() => new Date(fromYear, 0, 1), [fromYear]);
  const endMonth = useMemo(() => new Date(resolvedToYear, 11, 31), [resolvedToYear]);

  // Which month the calendar opens on. Prefer the selected value; when
  // empty, land on the first selectable month rather than today so a
  // future-bounded field doesn't open on a fully-disabled month.
  const landingMonth = useMemo((): Date => {
    if (selected) return selected;
    const today = new Date();
    if (minDate && today.getTime() < minDate.getTime()) return minDate;
    if (maxDate && today.getTime() > maxDate.getTime()) return maxDate;
    return today;
  }, [selected, minDate, maxDate]);

  // Merge minDate/maxDate with the caller-supplied disabled predicate.
  const disabledMatcher = useMemo((): ((date: Date) => boolean) | undefined => {
    const callerFn = typeof disabled === 'function' ? disabled : undefined;
    if (!callerFn && !minDate && !maxDate) return undefined;
    const minStart = minDate ? startOfDay(minDate).getTime() : null;
    const maxEnd = maxDate ? endOfDay(maxDate).getTime() : null;
    return (d: Date) => {
      if (callerFn?.(d)) return true;
      if (minStart !== null && d.getTime() < minStart) return true;
      if (maxEnd !== null && d.getTime() > maxEnd) return true;
      return false;
    };
  }, [disabled, minDate, maxDate]);

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <Label className={cn(typeof disabled === 'boolean' && disabled && 'opacity-60')}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={typeof disabled === 'boolean' ? disabled : false}
            className={cn(
              'w-full justify-start text-left font-normal',
              !selected && 'text-muted-foreground',
              error && 'border-destructive focus-visible:ring-destructive',
              triggerClassName,
            )}
            aria-invalid={!!error || undefined}
          >
            {icon ?? <CalendarIcon className="mr-2 h-4 w-4" />}
            {selected ? format(selected, fmt) : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto rounded-xl border border-zinc-200 p-0 shadow-lg"
          align="start"
        >
          <DayPicker
            mode="single"
            selected={selected ?? undefined}
            onSelect={handleSelect}
            disabled={disabledMatcher}
            defaultMonth={landingMonth}
            startMonth={startMonth}
            endMonth={endMonth}
            weekStartsOn={weekStartsOn}
            showOutsideDays
            className="relative bg-white p-3"
            classNames={{
              months: 'flex flex-col gap-3',
              month: 'space-y-1',
              // The caption row's height (h-9) matches the absolute nav row
              // height so chevrons and dropdown chips share a baseline.
              month_caption: 'relative flex h-9 items-center justify-center',
              caption_label: 'sr-only',

              // Nav row positioned absolutely on the caption baseline so the
              // chevrons flank the month/year chips horizontally.
              nav: 'pointer-events-none absolute inset-x-1 top-3 z-10 flex h-9 items-center justify-between',
              button_previous:
                'pointer-events-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:pointer-events-none disabled:opacity-30',
              button_next:
                'pointer-events-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:pointer-events-none disabled:opacity-30',

              // Grid
              month_grid: 'mt-2 w-full border-collapse',
              weekdays: 'flex',
              weekday:
                'flex h-7 w-9 items-center justify-center text-[11px] font-semibold uppercase tracking-wider text-zinc-400',
              week: 'mt-1 flex w-full',
              day: 'relative h-9 w-9 p-0 text-center focus-within:relative focus-within:z-20',
              day_button:
                'inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-sm font-normal text-zinc-700 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40',

              // State modifiers - `!` defeats day_button defaults regardless
              // of generated CSS order.
              selected:
                '!bg-blue-600 !font-medium !text-white !shadow-sm hover:!bg-blue-700 hover:!text-white',
              today: '!font-semibold !text-blue-600 ring-1 ring-inset ring-blue-500',
              outside: '!text-zinc-300 hover:!text-zinc-400',
              disabled: '!cursor-not-allowed !text-zinc-300 !opacity-40 hover:!bg-transparent',
              hidden: 'invisible',
            }}
            components={{
              Chevron: ({ orientation }) =>
                orientation === 'left' ? (
                  <ChevronLeft className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                ),
              MonthCaption: ({ calendarMonth }) => (
                <ModernMonthCaption
                  calendarMonth={calendarMonth}
                  fromYear={fromYear}
                  toYear={resolvedToYear}
                  showDropdown={showMonthYearDropdown}
                />
              ),
            }}
          />
          {showFooter && (
            <div className="flex items-center justify-between border-t border-zinc-100 bg-zinc-50/60 px-3 py-2">
              <button
                type="button"
                onClick={() => {
                  commit(new Date());
                  if (closeOnSelect) setOpen(false);
                }}
                className="rounded-md px-2 py-1 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-100"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => commit(null)}
                className="rounded-md px-2 py-1 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-100"
              >
                Clear
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {error ? (
        <p className="text-destructive text-xs font-medium">{error}</p>
      ) : hint ? (
        <p className="text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
};
